// Validation annotations and editor bookkeeping don't change the saved constraint rules.
const editorFields = new Set([
  'isError',
  'validationWarnings',
  'index',
  'rowKey',
  'lastmodified',
  'lastuser'
]);

const constraintContentKey = (constraints: unknown): string =>
  JSON.stringify(constraints, (_key, value) =>
    value && typeof value === 'object' && !Array.isArray(value)
      ? Object.fromEntries(
          Object.entries(value)
            .filter(([key]) => !editorFields.has(key))
            .sort(([a], [b]) => a.localeCompare(b))
        )
      : value
  );

export const countConstraintChanges = (saved: unknown, draft: unknown): number => {
  if (constraintContentKey(saved) === constraintContentKey(draft)) return 0;
  if (Array.isArray(saved) && Array.isArray(draft)) {
    // Match unchanged rows first so a deletion doesn't count every reindexed row as an edit.
    const remaining = saved.map(constraintContentKey);
    let changed = 0;
    for (const row of draft) {
      const index = remaining.indexOf(constraintContentKey(row));
      if (index < 0) {
        changed += 1;
      } else {
        remaining.splice(index, 1);
      }
    }
    return Math.max(remaining.length, changed);
  }
  if (saved && draft && typeof saved === 'object' && typeof draft === 'object') {
    const before = new Map(Object.entries(saved));
    const after = new Map(Object.entries(draft));
    return [...new Set([...before.keys(), ...after.keys()])]
      .filter(key => !editorFields.has(key))
      .reduce((count, key) => count + countConstraintChanges(before.get(key), after.get(key)), 0);
  }
  return 1;
};

export const getConstraintWarnings = (constraints: unknown): string[] => {
  if (!constraints || typeof constraints !== 'object') return [];
  return [
    ...new Set(
      Object.entries(constraints).flatMap(([key, value]) => {
        if (key === 'isError' && typeof value === 'string') return value.split('\n');
        if (key === 'validationWarnings' && Array.isArray(value)) {
          return value.filter(message => typeof message === 'string');
        }
        return getConstraintWarnings(value);
      })
    )
  ].filter(Boolean);
};
